import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Salad, Baby } from 'lucide-react';

const OBJETIVO_LABEL = {
  bajar_peso: 'Bajar de peso',
  subir_peso: 'Subir de peso',
  control_enfermedad: 'Control de enfermedad',
  mantenimiento: 'Mantenimiento',
  otro: 'Otro',
};

const ACTIVIDAD_LABEL = {
  sedentario: 'Sedentario', ligero: 'Ligero', moderado: 'Moderado', intenso: 'Intenso',
};

const COMPLICACION_LABEL = {
  ninguna: 'Ninguna',
  preeclampsia: 'Preeclampsia',
  eclampsia: 'Eclampsia',
  diabetes_gestacional: 'Diabetes gestacional',
};

// Fila etiqueta-valor; no renderiza nada si el valor está vacío
const Dato = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
};

/**
 * Resumen de solo lectura de la ficha nutriológica del paciente.
 * Solo muestra las secciones que tengan datos capturados.
 */
export const FichaNutriResumen = ({ patient }) => {
  const p = patient;

  const tieneIdentificacion = p.ocupacion || p.horas_fuera_casa || p.quien_prepara_alimentos ||
    p.donde_compra_alimentos || p.num_comidas_dia || p.objetivo || p.actividad_fisica;
  const tieneClinico = p.enfermedades_cronicas?.length > 0 || p.alergias_alimentarias?.length > 0 ||
    p.alimentos_no_gusta;
  const tieneEmbarazo = p.embarazo || p.lactancia;
  const hayAlgo = tieneIdentificacion || tieneClinico || tieneEmbarazo || p.diagnostico_nutricional;

  if (!hayAlgo) return null;

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Salad className="h-4 w-4 text-emerald-500" />
          Ficha nutriológica
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Identificación / estilo de vida */}
        {tieneIdentificacion && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Dato label="Objetivo" value={OBJETIVO_LABEL[p.objetivo]} />
            <Dato label="Actividad física" value={ACTIVIDAD_LABEL[p.actividad_fisica]} />
            <Dato label="Comidas al día" value={p.num_comidas_dia} />
            <Dato label="Ocupación" value={p.ocupacion} />
            <Dato label="Horas fuera de casa" value={p.horas_fuera_casa} />
            <Dato label="Prepara alimentos" value={p.quien_prepara_alimentos} />
            <Dato label="Compra alimentos en" value={p.donde_compra_alimentos} />
          </div>
        )}

        {/* Enfermedades / alergias / aversiones */}
        {tieneClinico && (
          <div className="space-y-2">
            {p.enfermedades_cronicas?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {p.enfermedades_cronicas.map(ec => (
                  <Badge key={ec} variant="outline" className="border-rose-300 text-rose-700 dark:text-rose-400">⚠️ {ec}</Badge>
                ))}
              </div>
            )}
            {p.alergias_alimentarias?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {p.alergias_alimentarias.map(al => (
                  <Badge key={al} variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">🚫 {al}</Badge>
                ))}
              </div>
            )}
            <Dato label="Alimentos que no le gustan" value={p.alimentos_no_gusta} />
          </div>
        )}

        {/* Embarazo / lactancia */}
        {tieneEmbarazo && (
          <div className="rounded-lg border border-pink-200 dark:border-pink-900 bg-pink-50/50 dark:bg-pink-950/10 p-3 space-y-2">
            <p className="text-sm font-medium text-pink-700 dark:text-pink-400 flex items-center gap-1.5">
              <Baby className="h-4 w-4" /> Embarazo / lactancia
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {p.embarazo && <Dato label="Embarazo" value={`Sí${p.meses_embarazo ? ` · ${p.meses_embarazo} meses` : ''}`} />}
              <Dato label="Peso previo al embarazo" value={p.peso_pre_embarazo ? `${p.peso_pre_embarazo} kg` : null} />
              <Dato label="Complicaciones" value={COMPLICACION_LABEL[p.complicaciones_embarazo]} />
              {p.lactancia && <Dato label="Lactancia" value={`Sí${p.meses_lactancia ? ` · ${p.meses_lactancia} meses` : ''}`} />}
            </div>
          </div>
        )}

        {/* Diagnóstico nutricional */}
        {p.diagnostico_nutricional && (
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Diagnóstico nutricional</span>
            <p className="text-sm whitespace-pre-wrap rounded-lg bg-muted/40 p-3">{p.diagnostico_nutricional}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
